import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  InputAdornment,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff, AdminPanelSettings } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import logo from "../../assets/img/logo.jpg";

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/admin";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_BASE}/login`, {
        email: email.trim(),
        password,
      });

      if (data?.user?.role !== "admin") {
        setError("Esta cuenta no tiene permisos para ingresar al panel.");
        return;
      }

      login(data.token, data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background:
          "radial-gradient(circle at top left, #1f4f46 0%, #132c33 45%, #0b1618 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(246,244,238,0.98) 100%)",
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 3,
              color: "#f7f1e8",
              background: "linear-gradient(135deg, #19352f 0%, #274b44 100%)",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src={logo}
                alt="Isaac Studio"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid rgba(255,255,255,0.35)",
                }}
              />
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 2.2 }}>
                  Panel Administrativo
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  Acceso seguro
                </Typography>
              </Box>
            </Stack>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3} component="form" onSubmit={handleSubmit}>
              <Stack spacing={1}>
                <Typography variant="h6" sx={{ color: "#172321", fontWeight: 700 }}>
                  Ingresá con una cuenta administradora
                </Typography>
                <Typography variant="body2" sx={{ color: "#5c6866" }}>
                  Solo usuarios con rol `admin` pueden entrar al panel.
                </Typography>
              </Stack>

              {error ? <Alert severity="error">{error}</Alert> : null}

              <TextField
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                fullWidth
                autoComplete="email"
              />

              <TextField
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                fullWidth
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AdminPanelSettings sx={{ color: "#274b44" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((value) => !value)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  background: "linear-gradient(135deg, #19352f 0%, #2d6254 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #142c27 0%, #244c42 100%)",
                  },
                }}
              >
                {loading ? "Ingresando..." : "Entrar al panel"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminLogin;
